import { offlineLine } from '@/show/showCache';
import { FEELING_COLORS, Transmission } from './StageHud';
import { fmtMB } from '@/show/localVideo';

export const videoStatus = (v) => {
  if (v.local && v.local.origin === 'file') return `VIDEO: FROM THIS COMPUTER ✓ (${v.local.name}, ${fmtMB(v.local.size)})`;
  if (v.local) return `VIDEO: ON THIS LAPTOP ✓ (${fmtMB(v.local.size)})`;
  if (v.unavailable) return `VIDEO: ${v.unavailable}`;
  if (v.error) return `VIDEO: OFFLINE · ${v.error}`;
  const d = v.dl || {};
  const saving = d.status === 'downloading' ? ` · SAVING ${Math.round((v.progress || 0) * 100)}% · ${fmtMB(d.bytes)}/${fmtMB(d.total)}` : d.status === 'error' ? ' · SAVE FAILED' : '';
  return `VIDEO: STREAMING (not saved)${saving}`;
};

const LandingButton = ({ testId, hotkey, label, sub, onClick, autoFocus }) => (
  <button
    type="button"
    data-testid={testId}
    autoFocus={autoFocus}
    onClick={onClick}
    className="landing-btn group flex min-h-[150px] w-[min(560px,42vw)] flex-col justify-between border-2 border-[var(--hair-strong)] bg-[var(--panel-2)]/80 p-7 text-left outline-none transition-[background-color,border-color,transform] duration-200 hover:-translate-y-1 hover:border-[var(--spore)] hover:bg-[#10b98122] focus-visible:-translate-y-1 focus-visible:border-[var(--spore)] focus-visible:bg-[#10b98122]"
  >
    <span className="label-eyebrow flex justify-between"><span>{sub}</span><span className="kbd px-2">{hotkey}</span></span>
    <span className="font-display text-4xl font-black leading-none text-[var(--ink)] group-hover:text-[var(--spore)] group-focus-visible:text-[var(--spore)] lg:text-5xl">{label}</span>
  </button>
);

export const HoldingScreen = ({ onStart }) => (
  <div data-testid="holding-screen" className="absolute inset-0 flex flex-col justify-between px-16 py-12">
    <div className="flex justify-between label-eyebrow">
      <span>[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ]</span>
      <span>INDUCTION COMPLETE · RESEARCH ASSISTANTS BRIEFED</span>
    </div>
    <div className="flex flex-col items-center gap-8">
      <div className="label-eyebrow">SPORE LAB · STAND BY FOR ZE PROFESSEUR</div>
      <button
        type="button"
        autoFocus
        data-testid="holding-start-button"
        onClick={onStart}
        className="landing-btn group border-2 border-[var(--spore)] bg-[var(--panel-2)]/80 px-20 py-12 outline-none transition-[background-color,transform] duration-200 hover:-translate-y-1 hover:bg-[#10b98122] focus-visible:-translate-y-1 focus-visible:bg-[#10b98122]"
      >
        <span data-testid="holding-title" className="home-title font-display text-6xl font-black leading-[0.9] lg:text-8xl">
          <span className="glow-spore">START EXPERIMENT</span>
        </span>
      </button>
      <div className="label-eyebrow text-[var(--dim)]"><span className="kbd px-2">SPACE</span> / <span className="kbd px-2">ENTER</span></div>
    </div>
    <div className="label-eyebrow text-right text-[var(--dim)]">THE SPORE PROJECT · RESEARCH &amp; CONTAINMENT SITE</div>
  </div>
);

export const TitleScreen = ({ snap, midiStatus, onVideo, onStart }) => {
  const p = snap.preload;
  const v = snap.video || {};
  const focusNext = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    const btns = [...e.currentTarget.querySelectorAll('button')];
    const i = btns.indexOf(document.activeElement);
    btns[(i + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? btns.length - 1 : 1)) % btns.length].focus();
    e.preventDefault();
  };
  return (
    <div data-testid="title-screen" className="pointer-events-none absolute inset-0 flex flex-col justify-between px-16 py-12">
      <div className="flex justify-between label-eyebrow">
        <span>[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ]</span>
        <span>THE SPORE PROJECT · RESEARCH &amp; CONTAINMENT SITE · SPORE LAB</span>
      </div>
      <div>
        <div className="label-eyebrow mb-6">SPORE LAB · GAME 2 · SPORE SAYS</div>
        <h1 data-testid="stage-title" className="home-title font-display text-6xl font-black leading-[0.9] lg:text-8xl">
          <span className="hive-stripes">SPORE Language Assessment —</span>
          <br />
          <span className="glow-spore">Feelings.</span>
        </h1>
        <p data-testid="title-subtitle" className="mt-6 text-lg tracking-[0.3em] text-[var(--muted)]">ONE SPECIMEN · ONE LANGUAGE · RESEARCH ASSISTANTS WANTED</p>
        <div data-testid="landing-buttons" role="group" aria-label="Start" onKeyDown={focusNext} className="pointer-events-auto mt-10 flex flex-wrap gap-8">
          <LandingButton testId="landing-video-button" hotkey="V" sub="INDUCTION · 2:45" label="TRAINING VIDEO" onClick={onVideo} autoFocus />
          <LandingButton testId="landing-start-button" hotkey="SPACE" sub="SPECIMEN TESTS" label="START EXPERIMENT FOR SPORE TESTS" onClick={onStart} />
        </div>
        {midiStatus === 'unsupported' && <p data-testid="stage-unsupported-message" className="mt-6 text-xl glow-amber">Open in Chrome or Edge on a desktop/laptop.</p>}
      </div>
      <div className="flex items-end justify-between">
        <div data-testid="preload-progress" data-done={p.done} data-total={p.total} className="w-[560px] text-xs tracking-[0.25em] text-[var(--dim)]">
          <div>DECODING SPECIMEN AUDIO {p.done}/{p.total}{p.complete ? ' · READY' : ''}</div>
          <div data-testid="preload-voices" className={`mt-1 ${p.voicesFailed ? 'text-[var(--amber)]' : ''}`}>VOICES {p.voices}/{p.voicesAvailable}{p.voicesFailed ? ` · ${p.voicesFailed} SUBTITLES ONLY` : ''}</div>
          {p.sfxAvailable !== undefined && <div data-testid="preload-sfx" className={`mt-1 ${p.sfxFailed ? 'text-[var(--amber)]' : ''}`}>SPECIMEN NOISES {p.sfx}/{p.sfxAvailable}</div>}
          <div className="mt-2 h-px bg-white/10"><div className="h-px bg-[var(--spore)] transition-[width] duration-500" style={{ width: `${(p.done / p.total) * 100}%` }} /></div>
          {snap.offline && <div data-testid="landing-offline-status" data-ready={snap.offline.ready} className={`mt-2 ${snap.offline.ready ? 'text-[var(--spore)]/70' : ''}`}>{offlineLine(snap.offline)}</div>}
        </div>
        <div data-testid="video-preload" data-ready={!!v.ready} data-progress={Math.round((v.progress || 0) * 100)} className={`text-right text-xs tracking-[0.25em] ${v.error ? 'text-[var(--amber)]' : 'text-[var(--dim)]'}`}>
          {videoStatus(v)}
        </div>
      </div>
    </div>
  );
};

export const RevealStamp = ({ snap }) => {
  const color = FEELING_COLORS[snap.feeling];
  const guess = snap.guesses[snap.chapterIndex] || 'GOOD';
  return (
    <div data-testid="reveal-stamp" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-10">
      <div data-testid="guess-stamp" className="stamp px-10 py-4 text-center" style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}>
        <div className="text-xs tracking-[0.5em] text-[var(--muted)]">FIELD DICTIONARY · PR. H. CHAMPIGNON</div>
        <div className="font-display mt-1 text-4xl font-extrabold tracking-[0.1em]">FEELING LOGGED: <span data-testid="guess-value">{guess}</span> (?)</div>
      </div>
      {snap.phaseMs > 1500 && (
        <div data-testid="analysis-stamp" className="stamp px-14 py-8 text-center" style={{ borderColor: color, color, boxShadow: `0 0 80px ${color}55, inset 0 0 40px ${color}22`, transform: 'rotate(2deg)' }}>
          <div className="text-sm tracking-[0.5em] text-[var(--ink)]">SPECTRAL ANALYSIS</div>
          <div data-testid="reveal-feeling" className="font-display mt-2 text-7xl font-extrabold tracking-[0.12em] lg:text-8xl" style={{ textShadow: `0 0 40px ${color}` }}>{snap.feeling.toUpperCase()}</div>
          <div className="mt-3 text-sm tracking-[0.4em] text-[var(--muted)]">ASSESSMENT {snap.unlocked.filter(Boolean).length}/6 {snap.forced ? '· OVERRIDE' : ''}</div>
        </div>
      )}
    </div>
  );
};

export const RevealStampLegacy = ({ snap }) => {
  const color = FEELING_COLORS[snap.feeling];
  return (
    <div data-testid="reveal-stamp" className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="stamp px-14 py-8 text-center" style={{ borderColor: color, color, boxShadow: `0 0 80px ${color}55, inset 0 0 40px ${color}22` }}>
        <div className="text-sm tracking-[0.5em] text-[var(--ink)]">FEELING IDENTIFIED</div>
        <div data-testid="reveal-feeling" className="font-display mt-2 text-7xl font-extrabold tracking-[0.12em] lg:text-9xl" style={{ textShadow: `0 0 40px ${color}` }}>{snap.feeling.toUpperCase()}</div>
        <div className="mt-3 text-sm tracking-[0.4em] text-[var(--muted)]">ASSESSMENT {snap.unlocked.filter(Boolean).length}/6 {snap.forced ? '· OVERRIDE' : ''}</div>
      </div>
    </div>
  );
};

export const EndScreen = ({ snap }) => (
  <div data-testid="end-screen" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-[#050807]/60 text-center">
    <div className="label-eyebrow">[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ] · STATUS: CONNECTED</div>
    <h2 className="font-display mt-6 text-6xl font-extrabold lg:text-8xl">Assessment <span className="glow-spore">complete.</span></h2>
    <div className="mt-8 flex gap-6 text-lg tracking-[0.2em]">
      {snap.feelings.map((f) => <span key={f} style={{ color: FEELING_COLORS[f] }}>{f.toUpperCase()}</span>)}
    </div>
    <p className="mt-8 text-sm tracking-[0.3em] text-[var(--muted)]">{snap.unlocked.filter(Boolean).length}/6 FEELINGS IDENTIFIED · THANK YOU FOR YOUR PARTICIPATION</p>
    <div className="mt-10 w-[min(760px,60vw)] text-left"><Transmission tx={snap.ui.transmission} /></div>
  </div>
);

export const PausedOverlay = () => (
  <div data-testid="paused-overlay" className="pointer-events-none absolute inset-x-0 top-1/3 text-center text-2xl tracking-[0.6em] text-[var(--amber)] dot-pulse">SIGNAL HOLD</div>
);

const Row = ({ k, v, testId, warn }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-0.5">
    <span className="text-[var(--muted)]">{k}</span>
    <span data-testid={testId} className={warn ? 'text-[var(--amber)]' : 'text-[var(--ink)]'}>{v}</span>
  </div>
);

export const HostHud = ({ snap, lab }) => {
  const st = lab.status;
  const color = st.status === 'connected' ? 'var(--spore)' : st.status === 'unsupported' || st.status === 'permission-denied' ? 'var(--coral)' : 'var(--amber)';
  const vq = snap.voiceQueue;
  return (
    <div data-testid="host-hud" className="pointer-events-none absolute right-6 top-36 w-[340px] border border-[var(--hair-strong)] bg-[#050807]/90 p-4 text-xs backdrop-blur-md">
      <div className="mb-2 flex items-center gap-2 tracking-[0.2em]"><span className="dot-live" style={{ background: color }} /> HOST · {st.status.toUpperCase()} {st.deviceName ? `· ${st.deviceName}` : ''}</div>
      {snap.hostPrompt && <div data-testid="host-prompt" className="mb-2 border border-[var(--amber)] p-2 text-center text-sm text-[var(--amber)] dot-pulse">PRESS N TO ADVANCE</div>}
      <Row k="Phase" v={`${snap.phase}${snap.paused ? ' (paused)' : ''}`} testId="host-phase" />
      <Row k="Chapter" v={`${snap.chapterIndex + 1}/6 ${snap.feelings[snap.chapterIndex]}`} />
      <Row k="Step" v={`${snap.stepIndex + 1}/${snap.steps.length} ${snap.step.label} · ${Math.round(snap.stepProgress * 100)}% · ${(snap.stepMs / 1000).toFixed(0)}s${snap.lenient ? ' · LENIENT' : ''}`} testId="host-timer" />
      <Row k="Touch" v={`${snap.touch.touchActive ? 'HUMAN' : 'background'} · score ${snap.touch.score.toFixed(1)} · ${snap.touch.lastClass || '—'}`} warn={snap.touch.learning} />
      <Row k="Base state" v={snap.touch.learning ? `LEARNING ${(snap.touch.learnRemainingMs / 1000).toFixed(1)}s` : `CC ${snap.touch.baseline.ccMean.toFixed(1)} ± ${snap.touch.baseline.ccStd.toFixed(1)}`} testId="host-baseline" />
      <Row k="Touch sensitivity" v={`${snap.sensitivity}/5`} testId="host-strictness" />
      <Row k="Script" v={`${snap.scriptId || 'built-in'}${snap.ui.generating ? ` · generating ${snap.ui.generating.voices}/${snap.ui.generating.total}` : ''}`} />
      <Row k="Voice source" v={snap.ui.transmission ? snap.ui.transmission.voiceSource || '—' : '—'} warn={snap.ui.transmission && snap.ui.transmission.voiceSource !== 'elevenlabs'} />
      <Row k="Preload" v={`${snap.preload.done}/${snap.preload.total} lines · ${(snap.preload.ms / 1000).toFixed(1)}s`} testId="host-preload" />
      {snap.net && <Row k="Network" v={`${snap.net.online ? (snap.net.slow ? 'SLOW' : 'ONLINE') : 'OFFLINE'} · OUTBOX ${snap.net.outbox}${snap.offline.ready ? ' · SAVED OFFLINE ✓' : ''}`} warn={!snap.net.online || snap.net.slow || snap.net.outbox > 0} testId="host-network" />}
      <Row k="Voices" v={`${snap.preload.voices}/${snap.preload.voicesAvailable} loaded${snap.preload.voicesFailed ? ` · ${snap.preload.voicesFailed} failed → subtitles` : ''}`} warn={snap.preload.voicesFailed > 0} testId="host-voices" />
      {snap.nextPreload && <Row k="Next audience" v={`${snap.nextPreload.done}/${snap.nextPreload.total}`} />}
      <Row k="Voice" v={vq.playing ? `${vq.playing.id} (${vq.playing.mode})` : 'idle'} />
      <Row k="Queued" v={vq.queued.join(', ') || '—'} />
      <Row k="Audio" v={snap.muted ? 'MUTED' : snap.audioReady ? 'on' : 'locked (press Space)'} warn={snap.muted || !snap.audioReady} />
      <Row k="Simulator" v={lab.sim.enabled ? 'ON' : 'off'} warn={lab.sim.enabled} />
      <div className="mt-2 text-[var(--muted)]">Channels</div>
      {lab.channels.length ? lab.channels.map((c) => <Row key={c.channel} k={`Ch ${c.channel}`} v={`${c.role} · ${c.ago < 0.6 ? 'active' : `${c.ago.toFixed(0)}s ago`}`} />) : <div className="text-[var(--dim)]">no events yet</div>}
    </div>
  );
};

const KEYS = [
  ['Space', 'start / pause-resume'], ['N', 'complete current step'], ['Shift+N', 'force-unlock experiment'], ['H', 'next coaching line'], ['1–6', 'jump to experiment'],
  ['↑ / ↓', 'advance / regress step'], ['[ / ]', 'touch sensitivity'], ['C', 'calibration'], ['B', 're-learn base state'], ['G', 'generate fresh script'], ['Shift+G', 'repair script (guesses + failed voices)'], ['S', 'simulator (Q stroke, W tap)'],
  ['M', 'mute / unmute'], ['D', 'host HUD'], ['?', 'this sheet'], ['F', 'fullscreen'], ['L', 'signal lab'], ['R ×2', 'reset show'],
];

export const CheatSheet = () => (
  <div data-testid="cheat-sheet" className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#050807]/80 backdrop-blur-md">
    <div className="grid grid-cols-2 gap-x-12 gap-y-3 border border-[var(--hair-strong)] p-10 text-lg">
      {KEYS.map(([k, d]) => (
        <div key={k} className="flex items-center gap-4"><span className="kbd min-w-[4.5rem] px-2">{k}</span><span className="text-[var(--muted)]">{d}</span></div>
      ))}
    </div>
  </div>
);

export const HostNote = ({ text }) => (
  <div data-testid="host-note" className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 border border-[var(--amber)] bg-[#050807]/90 px-4 py-1 text-xs tracking-[0.3em] text-[var(--amber)]">{text}</div>
);
